import * as React from "react";
import {
  NavigateOptions as RrNavigateOptions,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useNavigationStore } from "../stores/navigationStore";

export type NavigationOptions = {
  title?: string;
};
export type NavigateOptions = RrNavigateOptions & {
  searchParams?: Record<string, unknown>;
  data?: unknown;
};

function encodeQuery(params: Record<string, unknown>) {
  return `?${Object.entries(params)
    .map(([k, v]) => `${k}=${v}`)
    .join("&")}`;
}

export default function useNavigation() {
  const rrNavigate = useNavigate();
  const location = useLocation();
  const {
    setData,
    setOptions: setOptionsByPath,
    dataByPath,
    optionsByPath,
    routes,
  } = useNavigationStore();

  const data = dataByPath[location.pathname];
  const options = optionsByPath[location.pathname] as
    | NavigationOptions
    | undefined;

  const navigate = React.useCallback(
    (path: string, navOptions?: NavigateOptions) => {
      const { data, searchParams, ...rrOptions } = navOptions ?? {};
      const pathKey = path.toString();
      if (data !== undefined) setData(pathKey, data);
      const qs = searchParams ? encodeQuery(searchParams) : "";
      rrNavigate(`${path}${qs}`, rrOptions);
    },
    [rrNavigate, setData],
  );

  const setOptions = React.useCallback(
    (next: NavigationOptions) => {
      const current = useNavigationStore.getState().optionsByPath[
        location.pathname
      ] as NavigationOptions | undefined;
      setOptionsByPath(location.pathname, { ...current, ...next });
    },
    [location.pathname, setOptionsByPath],
  );

  const goBack = React.useCallback(() => rrNavigate(-1), [rrNavigate]);

  return {
    state: location.state,
    navigate,
    goBack,
    setOptions,
    data,
    options,
    dataByPath,
    optionsByPath,
    routes: routes.map((route) => ({
      ...route,
      data: dataByPath[route.path],
      options: optionsByPath[route.path],
    })),
  };
}
